// NZ date helpers — shared between web/ and admin/. Converts between ISO
// YYYY-MM-DD (the only shape stored or sent over the wire) and DD/MM/YYYY
// (the only shape shown to NZ users).
//
// Native <input type="date"> displays in the browser locale, so US-locale
// browsers render MM/DD/YYYY — that's the bug we're fixing. DateInputNZ
// uses these helpers to render and parse a plain text input instead.

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DISPLAY_RE = /^\s*(\d{1,2})\s*[/\-.]\s*(\d{1,2})\s*[/\-.]\s*(\d{2,4})\s*$/;

/** Convert ISO YYYY-MM-DD → DD/MM/YYYY. Returns '' for empty/invalid. */
export function isoToDisplay(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = ISO_RE.exec(iso);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

const WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Format raw keystrokes into a DD/MM/YYYY draft as the user types.
 *
 * The mobile numeric keypad has no `/` key, so a user can never type the
 * separators DD/MM/YYYY needs. This inserts them automatically: pure digits
 * auto-advance after the 2-digit day and 2-digit month (so "07032027" becomes
 * "07/03/2027"), while separators the user does type or paste (`/`, `-`, `.`)
 * are honoured as group boundaries — so shorthand like "7/3/2027" and pasted
 * dates survive untouched. Non-digit, non-separator characters are dropped.
 */
export function maskNzDateInput(raw: string): string {
  const chars = raw.replace(/[.\-]/g, '/').replace(/[^\d/]/g, '');
  const groups: string[] = [];
  const maxLens = [2, 2, 4];
  let cur = '';
  for (const ch of chars) {
    if (ch === '/') {
      if (cur !== '') {
        groups.push(cur);
        cur = '';
      }
      if (groups.length >= 3) break;
      continue;
    }
    cur += ch;
    const idx = groups.length;
    if (idx < 2 && cur.length === maxLens[idx]) {
      groups.push(cur);
      cur = '';
    }
  }
  if (cur !== '' && groups.length < 3) groups.push(cur);
  const out = groups.slice(0, 3);
  if (out[2]) out[2] = out[2].slice(0, 4);
  return out.join('/');
}

/**
 * Parse DD/MM/YYYY (or D/M/YY, with `/`, `-`, or `.` separators) → ISO.
 * Returns '' for empty input, ISO YYYY-MM-DD for valid, null for unparseable
 * or impossible (e.g. 31/02/2027). 2-digit years pivot through 2000.
 */
export function displayToIso(display: string): string | null {
  if (!display.trim()) return '';
  const m = DISPLAY_RE.exec(display);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  let year = parseInt(m[3], 10);
  if (year < 100) year += 2000;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
